/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import { formatCurrency } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import {
  Button,
  Center,
  CloseButton,
  rem,
  ScrollArea,
  SimpleGrid,
  Text,
} from '@mantine/core';
import type { ContextModalProps } from '@mantine/modals';
import {
  useGetProgressesSubscriptionDetail,
  type ProgressesSubscriptionDetail,
} from '../../api';
import { useCancelAutoRenew } from '../../api/use-cancel-auto-renew';
import IconCancelRenew from '@/assets/icons/cancel-subscription-icon.svg';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

type ModalCancelAutoRenewProps = ContextModalProps<{
  progressesId: ProgressesSubscriptionDetail['_id'];
  progressesData: ProgressesSubscriptionDetail;
}>;

function ModalCancelAutoRenew({
  context,
  innerProps,
}: ModalCancelAutoRenewProps) {
  const { progressesId, progressesData } = innerProps;

  const isMobile = useIsMobile();

  const {
    mutateAsync: cancelAutoRenew,
    isPending: isLoadingCancel,
    error: cancelError,
  } = useCancelAutoRenew();

  const { refetch: refetchProgressSubsDetail } =
    useGetProgressesSubscriptionDetail(progressesId as string, {
      enabled: false,
    });

  const onClose = () => {
    context.closeAll();
  };

  const onCancelAutoRenew = async (id: string) => {
    if (!id) return;

    try {
      const cancelRes = await cancelAutoRenew({ progresses_id: id });

      if (cancelRes.status === 'success') {
        toast.info({
          title: 'Đã huỷ gia hạn gói hội viên.',
          withIcon: true,
        });

        void refetchProgressSubsDetail();
      }
    } catch (error) {
      const errorData = (cancelError || error)?.response?.data || {
        title: 'Error',
        message: 'Error auto renew subscription.',
      };

      toast.error({
        title: errorData.title,
        message: errorData.message,
        withIcon: true,
      });
    } finally {
      onClose();
    }
  };

  return (
    <>
      <CloseButton
        pos="absolute"
        iconSize={rem(24)}
        variant="transparent"
        onClick={() => {
          context.closeAll();
        }}
        c="neutral.6"
        top={rem(16)}
        style={
          isMobile
            ? {
                left: rem(16),
                outline: 'none',
              }
            : {
                right: rem(16),
                outline: 'none',
              }
        }
      />
      <Center mt={24} mb={rem(24)}>
        <IconCancelRenew size={rem(148)} />
      </Center>

      <ScrollArea.Autosize mb={rem(16)}>
        <Text
          px={{ base: rem(16), sm: rem(24) }}
          size="md"
          fw={500}
          c="neutral.7"
          ta="center"
        >
          {progressesData?.total_discount > 0 ? (
            <>
              Bạn đã tiết kiệm{' '}
              <Text fw={700} component="span" c="neutral.7" span>
                {formatCurrency(progressesData?.total_discount)}
              </Text>{' '}
              nhờ gói hội viên này. Bạn có chắc chắn muốn hủy gia hạn?
            </>
          ) : (
            'Gói sẽ không được tự động gia hạn theo chu kỳ. Bạn có chắc chắn muốn hủy gia hạn?'
          )}
        </Text>
      </ScrollArea.Autosize>

      <SimpleGrid cols={1} w="100%" spacing="sm" mt={rem(8)}>
        <Button mih={rem(48)} fw={700} onClick={onClose}>
          Giữ lại
        </Button>
        <Button
          mih={rem(48)}
          c="neutral.7"
          bg="neutral.1"
          fw={700}
          loading={isLoadingCancel}
          onClick={() => onCancelAutoRenew(progressesId)}
        >
          Huỷ gia hạn
        </Button>
      </SimpleGrid>
    </>
  );
}

export default ModalCancelAutoRenew;
